/**
 * ImageViewer — full-screen photo viewer shared by complaint photos and
 * lead site photos. Adapted from the Visualiser lightbox: the photo is
 * fitted with object-contain (never cropped), previous / next buttons,
 * arrow keys and left / right swipes move between photos, a "2 / 5"
 * counter, Esc or the close button closes it. Pinch, mouse wheel,
 * double-click and the +/- buttons zoom; drag pans a zoomed photo.
 *
 * It is its own Radix Dialog, so it renders in a portal on <body>, traps
 * focus and gives focus back to the thumbnail that opened it. Rendered
 * inside another dialog (the complaint and lead dialogs), Radix treats it
 * as a nested layer: clicks and Esc inside the viewer do not close the
 * dialog underneath.
 *
 * Usage:
 *   const [openViewer, viewer] = useImageViewer();
 *   <PhotoThumb src={photoUrl(name, record.image_urls)} onClick={() => openViewer(urls, i)} />
 *   {viewer}
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from 'lucide-react';
import { API } from '../lib/store';

// Where an uploaded complaint / lead photo is served from. Those photos need
// sign-in, and an <img> cannot send the login token, so the API gives each
// record a signed link per photo (`image_urls` on complaints,
// `site_image_urls` on leads: { name: '/api/uploads/<name>?exp=..&sig=..' },
// valid about 12 hours). Pass that map as `urls`; the link is put under
// API so the platform sub-path build (REACT_APP_BACKEND_URL prefix) works.
export const photoUrl = (filename, urls) => {
  const signed = urls && typeof urls === 'object' ? urls[filename] : '';
  if (typeof signed === 'string' && signed.startsWith('/api/')) return `${API}${signed.slice(4)}`;
  return `${API}/uploads/${filename}`;
};

// All photo links of a record, in order: photoUrls(c.images, c.image_urls).
export const photoUrls = (names, urls) => (names || []).map((n) => photoUrl(n, urls));

const MAX_SCALE = 5;
const SWIPE_PX = 50;

const ctrl =
  'inline-flex h-11 w-11 min-h-0 items-center justify-center rounded-full ' +
  'transition focus:outline-none focus-visible:ring-2 focus-visible:ring-heda-gold';
const ctrlDark = `${ctrl} bg-white/10 text-white hover:bg-white/20`;

// `returnFocus` is a ref to the element to focus again on close (the
// thumbnail that opened the viewer); without a Dialog.Trigger, Radix has
// nothing to give focus back to on its own.
export default function ImageViewer({ images = [], startIndex = 0, open, onClose, title = 'Photo', returnFocus }) {
  const count = images.length;
  const [index, setIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const drag = useRef(null);       // { x, y } while panning
  const pinch = useRef(null);      // { dist, scale } while pinching
  const swipe = useRef(null);      // { x, y } single-finger start at scale 1

  const resetZoom = () => { setScale(1); setTx(0); setTy(0); };

  useEffect(() => {
    if (open) {
      setIndex(Math.min(Math.max(0, startIndex), Math.max(0, count - 1)));
      setScale(1); setTx(0); setTy(0);
    }
  }, [open, startIndex, count]);

  useEffect(() => { setLoaded(false); setScale(1); setTx(0); setTy(0); }, [index]);

  const go = useCallback((step) => {
    if (count < 2) return;
    setIndex((i) => (i + step + count) % count);
  }, [count]);

  const zoomBy = (d) => setScale((s) => {
    const next = Math.min(MAX_SCALE, Math.max(1, s + d));
    if (next === 1) { setTx(0); setTy(0); }
    return next;
  });

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    else if (e.key === '+' || e.key === '=') zoomBy(0.5);
    else if (e.key === '-') zoomBy(-0.5);
    else if (e.key === '0') resetZoom();
  };

  // ─── Mouse: wheel zoom, double-click toggle, drag to pan when zoomed.
  const onWheel = (e) => zoomBy(-e.deltaY / 500);
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    drag.current = { x: e.clientX, y: e.clientY };
  };
  const onMouseMove = (e) => {
    if (!drag.current) return;
    setTx((x) => x + (e.clientX - drag.current.x));
    setTy((y) => y + (e.clientY - drag.current.y));
    drag.current = { x: e.clientX, y: e.clientY };
  };
  const endDrag = () => { drag.current = null; };

  // ─── Touch: pinch to zoom, drag to pan when zoomed, swipe to change photo.
  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinch.current = { dist: dist(e.touches), scale };
      swipe.current = null;
      drag.current = null;
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      if (scale > 1) drag.current = { x: t.clientX, y: t.clientY };
      else swipe.current = { x: t.clientX, y: t.clientY };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinch.current) {
      const next = Math.min(MAX_SCALE, Math.max(1, pinch.current.scale * (dist(e.touches) / pinch.current.dist)));
      setScale(next);
      if (next === 1) { setTx(0); setTy(0); }
    } else if (e.touches.length === 1 && drag.current) {
      const t = e.touches[0];
      setTx((x) => x + (t.clientX - drag.current.x));
      setTy((y) => y + (t.clientY - drag.current.y));
      drag.current = { x: t.clientX, y: t.clientY };
    }
  };
  const onTouchEnd = (e) => {
    if (swipe.current && e.changedTouches.length) {
      const t = e.changedTouches[0];
      const dx = t.clientX - swipe.current.x;
      const dy = t.clientY - swipe.current.y;
      if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
    }
    if (e.touches.length === 0) { pinch.current = null; drag.current = null; swipe.current = null; }
  };

  if (!count) return null;
  const src = images[index] || images[0];
  const moving = !!(drag.current || pinch.current);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-black/95" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-[70] flex flex-col outline-none"
          aria-describedby={undefined}
          onKeyDown={onKeyDown}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            const el = returnFocus?.current;
            if (el && el.isConnected && typeof el.focus === 'function') el.focus();
          }}
          data-testid="image-viewer"
        >
          <DialogPrimitive.Title className="sr-only">{title} {index + 1} of {count}</DialogPrimitive.Title>

          {/* Top bar: close first so it gets focus when the viewer opens. */}
          <div
            className="absolute inset-x-0 top-0 z-10 flex flex-row-reverse items-center justify-between gap-2 px-3 pb-2"
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
          >
            <div className="flex flex-row-reverse items-center gap-2">
              <DialogPrimitive.Close
                className={`${ctrl} bg-white text-heda-ink hover:bg-white/90`}
                aria-label="Close"
                title="Close (Esc)"
                data-testid="image-viewer-close"
              >
                <X size={18} />
              </DialogPrimitive.Close>
              <button type="button" className={`${ctrlDark} hidden sm:inline-flex`} onClick={() => zoomBy(0.5)} aria-label="Zoom in" data-testid="image-viewer-zoom-in">
                <ZoomIn size={17} />
              </button>
              <button type="button" className={`${ctrlDark} hidden sm:inline-flex`} onClick={() => zoomBy(-0.5)} disabled={scale <= 1} aria-label="Zoom out" data-testid="image-viewer-zoom-out">
                <ZoomOut size={17} />
              </button>
            </div>
            <span
              className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold tabular-nums text-white"
              aria-live="polite"
              data-testid="image-viewer-counter"
            >
              {index + 1} / {count}
            </span>
          </div>

          <div
            className="relative flex flex-1 touch-none select-none items-center justify-center overflow-hidden px-2 pb-4 pt-[calc(env(safe-area-inset-top,0px)_+_68px)] sm:px-20"
            onWheel={onWheel}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onDoubleClick={() => (scale > 1 ? resetZoom() : setScale(2))}
          >
            {!loaded && <Loader2 className="absolute h-7 w-7 animate-spin text-heda-gold" aria-hidden="true" />}
            <img
              key={src}
              src={src}
              alt={`${title} ${index + 1} of ${count}`}
              draggable={false}
              onLoad={() => setLoaded(true)}
              onError={() => setLoaded(true)}
              className="block h-full w-full object-contain"
              style={{
                transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
                transition: moving ? 'none' : 'transform .15s ease-out',
                cursor: scale > 1 ? 'grab' : 'zoom-in',
              }}
              data-testid="image-viewer-img"
            />
          </div>

          {count > 1 && (
            <>
              <button
                type="button"
                className={`${ctrlDark} absolute left-2 top-1/2 z-10 h-12 w-12 -translate-y-1/2 sm:left-4`}
                onClick={() => go(-1)}
                aria-label="Previous photo"
                data-testid="image-viewer-prev"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                type="button"
                className={`${ctrlDark} absolute right-2 top-1/2 z-10 h-12 w-12 -translate-y-1/2 sm:right-4`}
                onClick={() => go(1)}
                aria-label="Next photo"
                data-testid="image-viewer-next"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// One state hook per screen: openViewer(urls, index) shows the viewer at
// that photo; render `viewer` once (inside the dialog that holds the
// thumbnails, when there is one).
export function useImageViewer(title) {
  const [state, setState] = useState(null);
  const opener = useRef(null);
  const openViewer = useCallback((images, index = 0) => {
    if (!images || !images.length) return;
    opener.current = document.activeElement;
    setState({ images, index });
  }, []);
  const viewer = (
    <ImageViewer
      images={state?.images || []}
      startIndex={state?.index || 0}
      open={!!state}
      onClose={() => setState(null)}
      title={title}
      returnFocus={opener}
    />
  );
  return [openViewer, viewer];
}

// A thumbnail that opens the viewer. Sized by the caller's className.
export function PhotoThumb({ src, onClick, className = 'w-16 h-16', label = 'Open photo', testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`block min-h-0 shrink-0 overflow-hidden rounded-xl border border-heda-line bg-heda-cream p-0 transition hover:border-heda-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-heda-gold ${className}`}
      aria-label={label}
      title={label}
      data-testid={testId}
    >
      <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
    </button>
  );
}
